import { useState, useEffect } from 'react';
import type { RefObject } from 'react';

export interface ChartDimensions {
  width: number;
  height: number;
}

/**
 * Hook to track container dimensions for responsive D3 charts
 *
 * Uses ResizeObserver to automatically update dimensions when container resizes.
 * Returns width and height that can be used for D3 scales and chart sizing.
 *
 * @param containerRef - React ref to the container element
 * @param aspectRatio - Optional aspect ratio to maintain (e.g., 16/9)
 * @returns Current width and height of the container
 *
 * @example
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { width, height } = useChartDimensions(containerRef);
 *
 * return (
 *   <div ref={containerRef}>
 *     <BarChart width={width} height={height} data={data} />
 *   </div>
 * );
 */
export function useChartDimensions(
  containerRef: RefObject<any>,
  aspectRatio?: number
): ChartDimensions {
  const [dimensions, setDimensions] = useState<ChartDimensions>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries[0]) return;

      const { width, height } = entries[0].contentRect;

      if (aspectRatio) {
        // Maintain aspect ratio by calculating height from width
        setDimensions({
          width,
          height: width / aspectRatio,
        });
      } else {
        setDimensions({ width, height });
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, aspectRatio]);

  return dimensions;
}
